import React, { useState, useEffect } from 'react';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle.js';
import Share2 from 'lucide-react/dist/esm/icons/share-2.js';
import Ban from 'lucide-react/dist/esm/icons/ban.js';
import { useAuth } from '../contexts/AuthContext';
import { useTranslations } from '../contexts/TranslationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatCurrencyValue } from '../utils/formatCurrency';
import { useCountdown } from '../hooks/useCountdown';
import { useCountdownStyle } from '../contexts/CountdownStyleContext';
import { useJackpotStyle } from '../contexts/JackpotStyleContext';
import CountdownTimerStyles from './CountdownTimerStyles';
import InlineTranslationEdit from './InlineTranslationEdit';
import DrawLifecycleMessage from './DrawLifecycleMessage';
import { getDrawLifecycleState, isDrawLifecycleBlocking, DRAW_LIFECYCLE_STATES } from '../utils/drawLifecycle';
import { DEFAULT_TARGET_CARD, formatTargetCardName } from '../utils/ctaHelpers';
import { normalizeGameType } from '../config/gameTypes';

export default function LiveDrawCard({ draw, onLoginClick, onBuyNowClick, onShareClick, hasAdditionalDraws, editMode, onTranslationUpdate, hideCountdown = false, targetCardName, gameType }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const displayTargetName = targetCardName || formatTargetCardName(DEFAULT_TARGET_CARD.rank, DEFAULT_TARGET_CARD.suit);
  const { t } = useTranslations();
  const normalizedGameType = normalizeGameType(gameType || draw?.game_type) || normalizeGameType(draw?.game_type) || gameType || draw?.game_type;
  const isPrizeDraw = normalizedGameType === 'prizeDraw' || draw?.is_prize === true || draw?.is_prize === 'true' || draw?.is_prize === 1;
  const { countdownStyle } = useCountdownStyle();
  const { timeLeft, shouldAnimate, isComplete } = useCountdown(draw, {
    enableAnimation: true
  });
  const [prevJackpot, setPrevJackpot] = useState(null);
  const [jackpotAnimating, setJackpotAnimating] = useState(false);
  const [particles, setParticles] = useState([]);
  const [animationIntensity, setAnimationIntensity] = useState('low');
  const [lastIncrease, setLastIncrease] = useState(0);
  const { jackpotStyle: userStylePreference } = useJackpotStyle();

  useEffect(() => {
    const currentJackpot = draw?.current_jackpot || 0;

    // Seed baseline once a positive jackpot value is loaded
    if (prevJackpot === null || prevJackpot === 0) {
      if (currentJackpot > 0) {
        setPrevJackpot(currentJackpot);
      }
      return;
    }

    if (currentJackpot > prevJackpot && prevJackpot > 0) {
      const increase = currentJackpot - prevJackpot;
      setLastIncrease(increase);

      let intensity = 'low';
      let particleCount = 3;

      // If user selected 'auto', determine intensity based on increase amount
      if (userStylePreference === 'auto') {
        if (increase >= 1000) {
          intensity = 'extreme';
          particleCount = 15;
        } else if (increase >= 500) {
          intensity = 'high';
          particleCount = 10;
        } else if (increase >= 100) {
          intensity = 'medium';
          particleCount = 6;
        } else {
          intensity = 'low';
          particleCount = 3;
        }
      } else {
        // Map user's style preference to intensity
        const styleToIntensity = {
          'pop': { intensity: 'low', particles: 3 },
          'tilt': { intensity: 'medium', particles: 6 },
          'surge': { intensity: 'high', particles: 10 },
          'explode': { intensity: 'extreme', particles: 15 }
        };
        const mapping = styleToIntensity[userStylePreference] || { intensity: 'low', particles: 3 };
        intensity = mapping.intensity;
        particleCount = mapping.particles;
      }

      setAnimationIntensity(intensity);
      createParticleBurst(particleCount);

      setJackpotAnimating(true);
      setPrevJackpot(currentJackpot);
      setTimeout(() => setJackpotAnimating(false), 1000);
    } else if (currentJackpot !== prevJackpot && currentJackpot > 0) {
      setPrevJackpot(currentJackpot);
    }
  }, [draw?.current_jackpot, prevJackpot, userStylePreference]);

  const createParticleBurst = (count) => {
    const newParticles = Array.from({ length: count }).map((_, i) => ({
      id: Date.now() + i,
      x: Math.random() * 80 - 40,
      y: Math.random() * 60 - 30,
      delay: Math.random() * 0.15,
      symbol: ['💵', '💰', '✨', '🌟'][Math.floor(Math.random() * 4)]
    }));
    setParticles(newParticles);
    setTimeout(() => setParticles([]), 1500);
  };

  const getJackpotGradient = () => {
    const amount = draw?.current_jackpot || 0;
    if (amount >= 100000) return 'from-red-600 via-orange-600 to-yellow-500';
    if (amount >= 50000) return 'from-orange-600 via-yellow-600 to-amber-500';
    if (amount >= 25000) return 'from-yellow-600 via-amber-600 to-orange-500';
    return 'from-green-600 to-emerald-600';
  };

  if (!draw) return null;

  const isValidDate = (d) => {
    if (!d) return false;
    const date = new Date(d);
    return !isNaN(date.getTime());
  };

  const lifecycleState = getDrawLifecycleState({
    draw,
    countdownComplete: isComplete,
    gameType: normalizedGameType || draw?.game_type,
  });
  const isLifecycleBlocking = isDrawLifecycleBlocking(lifecycleState);
  const isDrawClosed = (!lifecycleState && (!!draw.cutoff_date_passed || !isValidDate(draw.draw_date)))
    || lifecycleState === DRAW_LIFECYCLE_STATES.FINISHED;
  const isDrawInProgress = lifecycleState === DRAW_LIFECYCLE_STATES.STARTED
    || lifecycleState === DRAW_LIFECYCLE_STATES.WAITING
    || lifecycleState === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING
    || lifecycleState === DRAW_LIFECYCLE_STATES.EXPIRED;
  // Countdown just hit zero — draw is happening RIGHT NOW
  const isDrawingNow = (isComplete && !lifecycleState && !draw.cutoff_date_passed) || isDrawInProgress;

  const renderCountdown = () => {
    if (hideCountdown || isDrawClosed) return null;

    if (isDrawingNow) {
      return (
        <div className="mb-4 relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-4 shadow-lg shadow-purple-500/40">
          {/* Animated shimmer sweep */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_2s_linear_infinite]" style={{ backgroundSize: '200% 100%' }} />
          <div className="flex items-center justify-center gap-3">
            {/* Spinning draw wheel */}
            <div className="relative w-8 h-8 flex-shrink-0">
              <div className="absolute inset-0 rounded-full border-2 border-white/30" />
              <div className="absolute inset-0 rounded-full border-2 border-t-white border-r-white/50 border-b-transparent border-l-transparent animate-spin" />
              <div className="absolute inset-1 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-[10px]">🎯</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-white font-bold text-sm tracking-wide uppercase animate-pulse">
                Draw in Progress
              </p>
              <p className="text-white/70 text-xs mt-0.5">Results coming soon…</p>
            </div>
            {/* Pulsing dots */}
            <div className="flex gap-1 items-center">
              {[0, 0.2, 0.4].map((delay, i) => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/80 animate-bounce" style={{ animationDelay: `${delay}s` }} />
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (lifecycleState) {
      return (
        <div className="mb-4">
          <DrawLifecycleMessage state={lifecycleState} compact />
        </div>
      );
    }

    return (
      <div className="flex justify-center mb-4">
        <CountdownTimerStyles
          timeLeft={timeLeft}
          shouldAnimate={shouldAnimate}
          style={countdownStyle}
        />
      </div>
    );
  };

  const formatDrawDate = (dateString) => {
    if (!isValidDate(dateString)) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDrawTime = (dateString) => {
    if (!isValidDate(dateString)) return null;
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  const ticketsRemaining = (draw.max_tickets != null && draw.total_tickets_sold != null) ? draw.max_tickets - draw.total_tickets_sold : null;
  const percentageRemaining = draw.max_tickets ? (ticketsRemaining / draw.max_tickets) * 100 : null;

  const getUrgencyColor = () => {
    if (!percentageRemaining) return null;
    if (percentageRemaining <= 10) return 'red';
    if (percentageRemaining <= 30) return 'orange';
    return 'green';
  };

  const urgencyColor = getUrgencyColor();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-shadow">
      <div className="mb-6 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isDrawClosed ? (
              <>
                <div className="relative w-3 h-3 bg-gray-400 rounded-full"></div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  <InlineTranslationEdit
                    translationKey="liveDrawCard.closed"
                    value={t('liveDrawCard.closed')}
                    fallback="CLOSED"
                    onUpdate={onTranslationUpdate}
                    editMode={editMode}
                    className="text-xs font-semibold"
                  />
                </span>
              </>
            ) : isDrawInProgress ? (
              <>
                <div className="relative w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase">
                  <InlineTranslationEdit
                    translationKey="liveDrawCard.drawingInProgress"
                    value={t('liveDrawCard.drawingInProgress')}
                    fallback="DRAWING"
                    onUpdate={onTranslationUpdate}
                    editMode={editMode}
                    className="text-xs font-semibold"
                  />
                </span>
              </>
            ) : (
              <>
                <div className="relative">
                  <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
                  <div className="relative w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                  <InlineTranslationEdit
                    translationKey="liveDrawCard.live"
                    value={t('liveDrawCard.live')}
                    fallback="LIVE"
                    onUpdate={onTranslationUpdate}
                    editMode={editMode}
                    className="text-xs font-semibold"
                  />
                </span>
              </>
            )}
          </div>
          <button
            onClick={onShareClick}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            title="Share this draw"
          >
            <Share2 className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
          </button>
        </div>
        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2 mt-3">
          {draw.title}
        </h3>

        {isDrawClosed ? (
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl p-4 border border-gray-300 dark:border-gray-600 text-center">
            <Ban className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
              <InlineTranslationEdit
                translationKey="liveDrawCard.drawClosedTitle"
                value={t('liveDrawCard.drawClosedTitle')}
                fallback="Draw Currently Closed"
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="text-sm font-semibold"
              />
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              <InlineTranslationEdit
                translationKey="liveDrawCard.drawClosedDesc"
                value={t('liveDrawCard.drawClosedDesc')}
                fallback="The next draw date has not been scheduled yet. Please check back soon!"
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="text-xs"
              />
            </p>
          </div>
        ) : (
          <>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <InlineTranslationEdit
                translationKey="liveDrawCard.drawDate"
                value={t('liveDrawCard.drawDate')}
                fallback="Draw Date"
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="text-xs"
              />
            </h4>
            <p className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-400 dark:to-cyan-400 bg-clip-text text-transparent">
              {formatDrawDate(draw.draw_date)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">at {formatDrawTime(draw.draw_date)}</p>
          </>
        )}
        {draw.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{draw.description}</p>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
        {isPrizeDraw ? (
          <>
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              <InlineTranslationEdit
                translationKey="liveDrawCard.featuredPrize"
                value={t('liveDrawCard.featuredPrize')}
                fallback="Featured Prize"
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="text-sm font-semibold"
              />
            </h3>
            {renderCountdown()}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-700 mb-6 text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                {draw.prize_description || 'Amazing Prize'}
              </div>
              {draw.prize_value && (
                <div className="text-lg text-purple-500 dark:text-purple-300">
                  Value: {formatCurrencyValue(draw.prize_value, { language })}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Divider above countdown */}
            <hr className="border-0 h-px w-full mb-6" style={{ backgroundColor: 'var(--charity-primary, #3b82f6)', opacity: 0.2 }} />

            {renderCountdown()}

            {/* Divider below countdown */}
            <hr className="border-0 h-px w-full mt-6 mb-6" style={{ backgroundColor: 'var(--charity-primary, #3b82f6)', opacity: 0.2 }} />

            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              <InlineTranslationEdit
                translationKey={isPrizeDraw ? 'liveDrawCard.grandPrize' : 'liveDrawCard.nextJackpot'}
                value={t(isPrizeDraw ? 'liveDrawCard.grandPrize' : 'liveDrawCard.nextJackpot')}
                fallback={isPrizeDraw ? 'Grand Prize' : 'Next Jackpot'}
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="text-sm font-semibold"
              />
            </h3>
            <div className="relative overflow-visible flex justify-center">
              {isPrizeDraw ? (
                <>
                  <div className="relative text-center">
                    <div className={`text-7xl mb-3 transition-all duration-500 ${
                      jackpotAnimating ? 'animate-bounce scale-110' : 'scale-100'
                    }`}>
                      💻
                    </div>
                    <div className={`text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-all duration-500 ${
                      jackpotAnimating ? 'scale-110' : 'scale-100'
                    }`}>
                      {draw.prize_name || draw.prize_description || draw.title || 'Amazing Prize'}
                    </div>
                    <div className="text-lg font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 bg-clip-text text-transparent mb-4">
                      Est. Value: {formatCurrencyValue(draw.prize_value || draw.current_jackpot || 0, { language })}
                    </div>

                    {/* Particle effects for prize */}
                    {particles.map((particle) => (
                      <div
                        key={particle.id}
                        className="absolute text-xl animate-money-rain pointer-events-none z-10"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: `translate(${particle.x}px, ${particle.y}px)`,
                          animationDelay: `${particle.delay}s`
                        }}
                      >
                        {particle.symbol}
                      </div>
                    ))}
                  </div>

                  {/* Floating increase amount for prizes */}
                  {jackpotAnimating && lastIncrease > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-2xl font-bold text-purple-500 animate-value-increment">
                        Prize Upgraded! 🎁
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="relative inline-block">
                    <div className={`text-5xl font-bold bg-gradient-to-r ${isDrawingNow ? 'from-violet-500 via-purple-500 to-indigo-500' : getJackpotGradient()} dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent mb-6 transition-all duration-500 cursor-default ${
                      isDrawingNow ? 'animate-pulse scale-105' :
                      jackpotAnimating
                        ? `animate-jackpot-${animationIntensity === 'extreme' ? 'explode' : animationIntensity === 'high' ? 'surge' : animationIntensity === 'medium' ? 'tilt' : 'pop'}`
                        : 'scale-100'
                    }`}
                    style={{
                      filter: isDrawingNow
                        ? 'drop-shadow(0 0 20px rgba(139, 92, 246, 0.8)) drop-shadow(0 0 40px rgba(167, 139, 250, 0.4))'
                        : jackpotAnimating
                        ? 'drop-shadow(0 0 20px rgba(34, 197, 94, 0.8)) drop-shadow(0 0 30px rgba(250, 204, 21, 0.5))'
                        : 'drop-shadow(0 0 5px rgba(34, 197, 94, 0.3))',
                      transition: 'filter 0.5s ease-in-out'
                    }}>
                      {formatCurrencyValue(draw.current_jackpot || 0, { language })}
                    </div>
                    {/* "Drawing now" orbit ring around jackpot */}
                    {isDrawingNow && (
                      <div className="absolute inset-0 -m-3 rounded-full border border-violet-400/40 animate-[spin_3s_linear_infinite]" />
                    )}

                    {/* Particle effects for non-prize draws */}
                    {particles.map((particle) => (
                      <div
                        key={particle.id}
                        className="absolute text-xl animate-money-rain pointer-events-none z-10"
                        style={{
                          left: '50%',
                          top: '50%',
                          transform: `translate(${particle.x}px, ${particle.y}px)`,
                          animationDelay: `${particle.delay}s`
                        }}
                      >
                        {particle.symbol}
                      </div>
                    ))}

                    {/* Glow rings for high-value increases */}
                    {jackpotAnimating && animationIntensity !== 'low' && (
                      <>
                        <div className="absolute inset-0 -m-4 rounded-full border-2 border-green-400/40 animate-ripple-wave"></div>
                        <div className="absolute inset-0 -m-4 rounded-full border-2 border-yellow-400/40 animate-ripple-wave" style={{ animationDelay: '0.15s' }}></div>
                      </>
                    )}
                  </div>

                  {/* Floating increase amount */}
                  {jackpotAnimating && lastIncrease > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className={`font-bold animate-value-increment ${
                        animationIntensity === 'extreme' ? 'text-3xl text-orange-500' :
                        animationIntensity === 'high' ? 'text-2xl text-yellow-500' :
                        'text-xl text-green-500'
                      }`}>
                        +{formatCurrencyValue(lastIncrease, { language })}
                      </div>
                    </div>
                  )}

                  {/* Sparkle burst for extreme increases */}
                  {jackpotAnimating && animationIntensity === 'extreme' && (
                    <>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div
                          key={`sparkle-${i}`}
                          className="absolute text-yellow-400 text-2xl animate-sparkle-burst"
                          style={{
                            left: '50%',
                            top: '50%',
                            transform: `rotate(${i * 45}deg) translateY(-40px)`,
                            animationDelay: `${i * 0.05}s`
                          }}
                        >
                          ✨
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
            
            {/* Secondary Pot - for Chase the Ace */}
            {draw.game_type === 'chaseTheAce' && draw.consolation_prize > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                    Weekly Consolation Prize
                  </p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {formatCurrencyValue(draw.consolation_prize || 0, { language })}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Win even if you don't find the {displayTargetName}!
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cards Remaining - for elimination and chase the ace draws */}
      {(draw.game_type === 'eliminationDraw' || draw.game_type === 'chaseTheAce') && draw.cards_remaining != null && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-4 border-2 border-amber-300 dark:border-amber-700">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {draw.game_type === 'chaseTheAce' ? 'Cards Remaining' : 'Tickets Still In Play'}
              </span>
              <span className="text-xs px-2 py-1 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded-full font-medium">
                {draw.game_type === 'chaseTheAce' ? 'Find the Ace!' : 'Last One Wins!'}
              </span>
            </div>
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <div className="text-4xl font-bold text-amber-600 dark:text-amber-400">
                  {draw.cards_remaining}
                </div>
                <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  {draw.game_type === 'chaseTheAce' ? 'Cards Left' : 'Remaining'}
                </div>
              </div>
              {draw.eliminated_cards && (
                <div className="text-center border-l border-amber-300 dark:border-amber-600 pl-4">
                  <div className="text-4xl font-bold text-gray-400 dark:text-gray-500">
                    {draw.eliminated_cards.length}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Eliminated
                  </div>
                </div>
              )}
            </div>
            {draw.game_type === 'eliminationDraw' && (
              <div className="mt-3 w-full bg-amber-200 dark:bg-amber-800 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(draw.cards_remaining / (draw.cards_remaining + (draw.eliminated_cards?.length || 0))) * 100}%` }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
        <div className="grid grid-cols-2 gap-4">
          {draw.total_tickets_sold != null && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{draw.total_tickets_sold.toLocaleString()}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 uppercase mt-1">
              <InlineTranslationEdit
                translationKey="liveDrawCard.totalTickets"
                value={t('liveDrawCard.totalTickets')}
                fallback="Total Tickets"
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="text-xs uppercase"
              />
            </div>
          </div>
          )}
          {(draw.total_players ?? draw.total_tickets_sold) != null && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{(draw.total_players ?? draw.total_tickets_sold).toLocaleString()}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 uppercase mt-1">
              <InlineTranslationEdit
                translationKey="liveDrawCard.totalPlayers"
                value={t('liveDrawCard.totalPlayers')}
                fallback="Total Players"
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="text-xs uppercase"
              />
            </div>
          </div>
          )}
        </div>
      </div>

      {ticketsRemaining !== null && (
        <div className={`border-t border-gray-200 dark:border-gray-700 pt-6 mb-6 ${
          urgencyColor === 'red' ? 'animate-pulse' : ''
        }`}>
          <div className={`rounded-xl p-4 ${
            urgencyColor === 'red'
              ? 'bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border-2 border-red-300 dark:border-red-700'
              : urgencyColor === 'orange'
              ? 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-2 border-orange-300 dark:border-orange-700'
              : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Ticket className={`w-5 h-5 ${
                  urgencyColor === 'red'
                    ? 'text-red-600 dark:text-red-400'
                    : urgencyColor === 'orange'
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-green-600 dark:text-green-400'
                }`} />
                <h4 className={`font-bold ${
                  urgencyColor === 'red'
                    ? 'text-red-900 dark:text-red-100'
                    : urgencyColor === 'orange'
                    ? 'text-orange-900 dark:text-orange-100'
                    : 'text-green-900 dark:text-green-100'
                }`}>
                  <InlineTranslationEdit
                    translationKey="liveDrawCard.ticketsRemaining"
                    value={t('liveDrawCard.ticketsRemaining')}
                    fallback="Tickets Remaining"
                    onUpdate={onTranslationUpdate}
                    editMode={editMode}
                    className="font-bold"
                  />
                </h4>
              </div>
              {urgencyColor === 'red' && (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 animate-bounce" />
              )}
            </div>

            <div className="flex items-baseline justify-between mb-2">
              <div className={`text-3xl font-bold ${
                urgencyColor === 'red'
                  ? 'text-red-600 dark:text-red-400'
                  : urgencyColor === 'orange'
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-green-600 dark:text-green-400'
              }`}>
                {ticketsRemaining.toLocaleString()}
              </div>
              <div className={`text-sm font-semibold ${
                urgencyColor === 'red'
                  ? 'text-red-700 dark:text-red-300'
                  : urgencyColor === 'orange'
                  ? 'text-orange-700 dark:text-orange-300'
                  : 'text-green-700 dark:text-green-300'
              }`}>
                {percentageRemaining.toFixed(1)}% left
              </div>
            </div>

            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  urgencyColor === 'red'
                    ? 'bg-gradient-to-r from-red-500 to-rose-500'
                    : urgencyColor === 'orange'
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500'
                }`}
                style={{ width: `${percentageRemaining}%` }}
              />
            </div>

            {urgencyColor === 'red' && (
              <p className="text-xs text-red-800 dark:text-red-200 mt-3 font-semibold text-center">
                🔥 Almost sold out! Don't miss your chance!
              </p>
            )}
            {urgencyColor === 'orange' && (
              <p className="text-xs text-orange-800 dark:text-orange-200 mt-3 font-semibold text-center">
                ⚡ Selling fast! Get your tickets now!
              </p>
            )}
          </div>
        </div>
      )}

      {isDrawingNow ? (
        /* ── Drawing in Progress — creative disabled state ── */
        <div className="w-full relative overflow-hidden rounded-xl">
          {/* Glowing animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 animate-[gradient-shift_3s_ease_infinite]" style={{ backgroundSize: '200% 100%' }} />
          {/* Scanline shimmer */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-[shimmer_1.8s_linear_infinite]" style={{ backgroundSize: '200% 100%' }} />
          <div className="relative py-4 px-5 flex items-center justify-center gap-3 cursor-not-allowed select-none">
            {/* Spinning icon */}
            <div className="relative w-6 h-6 flex-shrink-0">
              <div className="absolute inset-0 rounded-full border-2 border-white/30" />
              <div className="absolute inset-0 rounded-full border-2 border-t-white border-r-white/40 border-b-transparent border-l-transparent animate-spin" />
            </div>
            <span className="text-white font-bold text-base tracking-wide">
              <InlineTranslationEdit
                translationKey="liveDrawCard.drawingInProgress"
                value={t('liveDrawCard.drawingInProgress')}
                fallback="Drawing in Progress…"
                onUpdate={onTranslationUpdate}
                editMode={editMode}
                className="font-bold"
              />
            </span>
            {/* Pulsing dots */}
            <div className="flex gap-0.5 items-center">
              {[0, 0.15, 0.3].map((delay, i) => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: `${delay}s` }} />
              ))}
            </div>
          </div>
        </div>
      ) : isDrawClosed || isLifecycleBlocking ? (
        <div className="w-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-semibold py-4 rounded-xl text-center cursor-not-allowed">
          <InlineTranslationEdit
            translationKey="liveDrawCard.salesClosed"
            value={t('liveDrawCard.salesClosed')}
            fallback="Sales Closed"
            onUpdate={onTranslationUpdate}
            editMode={editMode}
            className="font-semibold"
          />
        </div>
      ) : (
        <button
          onClick={(e) => {
            if (editMode) {
              e.preventDefault();
              return;
            }
            if (user && onBuyNowClick) {
              onBuyNowClick();
            } else if (onLoginClick) {
              onLoginClick();
            }
          }}
          className={`w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-4 rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/50 hover:scale-105 active:scale-95 relative overflow-hidden group ${editMode ? 'cursor-default' : ''}`}
        >
          <span className="relative z-10">
            <InlineTranslationEdit
              translationKey="liveDrawCard.buyNow"
              value={t('liveDrawCard.buyNow')}
              fallback="Buy Now"
              onUpdate={onTranslationUpdate}
              editMode={editMode}
              className="font-bold"
            />
          </span>
          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
        </button>
      )}

      {hasAdditionalDraws && (
        <div className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-3 border border-amber-200 dark:border-amber-700 text-center">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            <InlineTranslationEdit
              translationKey="liveDrawCard.additionalDraws"
              value={t('liveDrawCard.additionalDraws')}
              fallback="✨ Additional Draws Active"
              onUpdate={onTranslationUpdate}
              editMode={editMode}
              className="text-sm font-semibold"
            />
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
            <InlineTranslationEdit
              translationKey="liveDrawCard.bonusEntryDesc"
              value={t('liveDrawCard.bonusEntryDesc')}
              fallback="See below for bonus entry opportunities"
              onUpdate={onTranslationUpdate}
              editMode={editMode}
              className="text-xs"
            />
          </p>
        </div>
      )}
    </div>
  );
}
